import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import PageTitle from "../../layouts/PageTitle";
import {
  useGetVehicleExpenseSummariesQuery,
  useGetVehicleSummaryForChartQuery,
} from "../../../store/appApi";
import { selectHasModuleAccess } from "../../../store/authSlice";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const QUARTER_MAP = {
  Q1: [0, 1, 2],
  Q2: [3, 4, 5],
  Q3: [6, 7, 8],
  Q4: [9, 10, 11],
};
// Colors — distinct from Labour pages (navy+teal & purple+amber)
const C_CURRENT = "#324336"; // brand purple
const C_PRIOR = "#f97316"; // orange

const fmtINR = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const sumArr = (arr) => arr.reduce((a, b) => a + b, 0);
const weekBucket = (day) => (day <= 7 ? 0 : day <= 14 ? 1 : day <= 21 ? 2 : 3);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e9ecef",
        borderRadius: 6,
        padding: "10px 14px",
        fontSize: 12,
      }}
    >
      <p style={{ margin: "0 0 6px", fontWeight: 600, color: "#495057" }}>
        {label}
      </p>
      {payload.map((entry) => (
        <p key={entry.name} style={{ margin: "2px 0", color: entry.color }}>
          {entry.name}: <strong>{fmtINR(entry.value)}</strong>
        </p>
      ))}
    </div>
  );
};

const NoData = ({ year }) => (
  <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
    <svg
      width="52"
      height="52"
      viewBox="0 0 52 52"
      fill="none"
      style={{ opacity: 0.35, marginBottom: 12 }}
    >
      <circle cx="26" cy="26" r="25" stroke="#6c757d" strokeWidth="2" />
      <path
        d="M16 36 L26 16 L36 36"
        stroke="#6c757d"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="26" cy="40" r="2" fill="#6c757d" />
    </svg>
    <p className="mb-0 small">
      No expense data recorded for <strong>{year}</strong>
    </p>
  </div>
);

const VehicleSummary = () => {
  const navigate = useNavigate();
  // Access follows whatever the "finance" module permission super-admin
  // granted this staff member at creation/edit time — same gate the backend
  // route (financeRead) enforces, so this never disagrees with the API.
  const hasAccess = useSelector(selectHasModuleAccess("finance", "read"));

  const { data, isLoading, isFetching, isError } =
    useGetVehicleExpenseSummariesQuery(undefined, { skip: !hasAccess });

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const grandTotal = Number(data?.grandTotal) || 0;
  const loading = isLoading || isFetching;
  const fmt = (n) => Number(n).toLocaleString("en-IN");

  // All vehicle expense transactions for chart aggregation
  const { data: chartRaw } = useGetVehicleSummaryForChartQuery(undefined, { skip: !hasAccess });
  const allTxns = Array.isArray(chartRaw?.finance) ? chartRaw.finance : [];

  const YEAR_OPTIONS = useMemo(() => {
    const currentYear = new Date().getFullYear();
    if (!allTxns.length) return [currentYear];
    const years = new Set(allTxns.map((t) => new Date(t.created).getFullYear()));
    years.add(currentYear);
    return [...years].sort((a, b) => a - b);
  }, [allTxns]);

  // Chart 1 — comparison
  const [chartYear, setChartYear] = useState(new Date().getFullYear());
  const [chartQuarter, setChartQuarter] = useState("all");
  const [chartMonth, setChartMonth] = useState("all");
  const [comparisonMode, setComparisonMode] = useState("YoY");

  const { chartData, currentLabel, priorLabel, currentTotal, priorTotal } =
    useMemo(() => {
      const yr = chartYear;
      const prevYr = yr - 1;

      if (comparisonMode === "YoY") {
        const currB = Array(12).fill(0);
        const priorB = Array(12).fill(0);
        allTxns.forEach(({ created, amount }) => {
          const d = new Date(created);
          const m = d.getMonth();
          if (d.getFullYear() === yr) currB[m] += Number(amount || 0);
          else if (d.getFullYear() === prevYr) priorB[m] += Number(amount || 0);
        });
        return {
          chartData: MONTHS.map((label, i) => ({
            label,
            current: currB[i],
            prior: priorB[i],
          })),
          currentLabel: String(yr),
          priorLabel: String(prevYr),
          currentTotal: sumArr(currB),
          priorTotal: sumArr(priorB),
        };
      }

      if (comparisonMode === "QoQ") {
        const qKey =
          chartQuarter === "all"
            ? `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`
            : chartQuarter;
        const qNum = parseInt(qKey[1], 10);
        const prevQKey = qNum === 1 ? "Q4" : `Q${qNum - 1}`;
        const prevQYear = qNum === 1 ? yr - 1 : yr;
        const currIdxs = QUARTER_MAP[qKey];
        const prevIdxs = QUARTER_MAP[prevQKey];
        const currB = Array(3).fill(0);
        const priorB = Array(3).fill(0);
        allTxns.forEach(({ created, amount }) => {
          const d = new Date(created);
          const y = d.getFullYear();
          const m = d.getMonth();
          const ci = currIdxs.indexOf(m);
          const pi = prevIdxs.indexOf(m);
          if (y === yr && ci !== -1) currB[ci] += Number(amount || 0);
          if (y === prevQYear && pi !== -1) priorB[pi] += Number(amount || 0);
        });
        return {
          chartData: currIdxs.map((mi, pos) => ({
            label: MONTHS[mi],
            current: currB[pos],
            prior: priorB[pos],
          })),
          currentLabel: `${qKey} ${yr}`,
          priorLabel: `${prevQKey} ${prevQYear}`,
          currentTotal: sumArr(currB),
          priorTotal: sumArr(priorB),
        };
      }

      // MoM
      const mIdx =
        chartMonth === "all"
          ? new Date().getMonth()
          : parseInt(chartMonth, 10) - 1;
      const prevMIdx = mIdx === 0 ? 11 : mIdx - 1;
      const prevMYear = mIdx === 0 ? yr - 1 : yr;
      const currB = Array(4).fill(0);
      const priorB = Array(4).fill(0);
      allTxns.forEach(({ created, amount }) => {
        const d = new Date(created);
        const y = d.getFullYear();
        const m = d.getMonth();
        const w = weekBucket(d.getDate());
        if (y === yr && m === mIdx) currB[w] += Number(amount || 0);
        if (y === prevMYear && m === prevMIdx) priorB[w] += Number(amount || 0);
      });
      const WEEK_LABELS = ["Week 1", "Week 2", "Week 3", "Week 4"];
      return {
        chartData: WEEK_LABELS.map((label, i) => ({
          label,
          current: currB[i],
          prior: priorB[i],
        })),
        currentLabel: `${MONTHS[mIdx]} ${yr}`,
        priorLabel: `${MONTHS[prevMIdx]} ${prevMYear}`,
        currentTotal: sumArr(currB),
        priorTotal: sumArr(priorB),
      };
    }, [allTxns, chartYear, chartQuarter, chartMonth, comparisonMode]);

  const rawChange =
    priorTotal > 0
      ? (((currentTotal - priorTotal) / priorTotal) * 100).toFixed(1)
      : null;
  const changeUp = rawChange !== null && Number(rawChange) >= 0;

  // Chart 2 — trend
  const [trendYear, setTrendYear] = useState(new Date().getFullYear());

  const { trendData, trendTotal, trendHasData } = useMemo(() => {
    const buckets = Array(12).fill(0);
    allTxns.forEach(({ created, amount }) => {
      const d = new Date(created);
      if (d.getFullYear() === trendYear) buckets[d.getMonth()] += Number(amount || 0);
    });
    const hasData = buckets.some((v) => v > 0);
    const lastFilledIdx = buckets.reduce(
      (last, v, i) => (v > 0 ? i : last),
      -1,
    );
    const visibleCount =
      lastFilledIdx === 11 ? 12 : Math.min(lastFilledIdx + 2, 12);
    const slicedMonths = hasData ? MONTHS.slice(0, visibleCount) : MONTHS;
    return {
      trendData: slicedMonths.map((label, i) => ({
        label,
        expense: buckets[i] || null,
      })),
      trendTotal: sumArr(buckets),
      trendHasData: hasData,
    };
  }, [allTxns, trendYear]);

  if (!hasAccess) {
    return (
      <>
        <PageTitle activeMenu="Vehicle Report" motherMenu="Finance" />
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <div className="card">
              <div className="card-body text-center py-5">
                <i className="fa fa-lock fa-3x text-warning mb-3 d-block" />
                <h5>Access Restricted</h5>
                <p className="text-muted mb-0">
                  You don't have access to the Finance module. Contact your
                  administrator to request access.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <PageTitle activeMenu="Vehicle Report" motherMenu="Finance" />

      <div className="row">
        {/* ══ CHART 1 — Comparison (YoY / QoQ / MoM) ══ */}
        <div className="col-lg-6 d-flex flex-column">
          <div className="card mb-4 h-100">
            {/* Filter row */}
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{ background: "#fff", borderBottom: "1px solid #f0f2f5" }}
            >
              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={chartYear}
                  onChange={(e) => setChartYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Quarter
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 132 }}
                  value={chartQuarter}
                  onChange={(e) => setChartQuarter(e.target.value)}
                  disabled={comparisonMode === "MoM"}
                >
                  <option value="all">All Quarters</option>
                  <option value="Q1">Q1 (Jan–Mar)</option>
                  <option value="Q2">Q2 (Apr–Jun)</option>
                  <option value="Q3">Q3 (Jul–Sep)</option>
                  <option value="Q4">Q4 (Oct–Dec)</option>
                </select>
              </div>

              <div className="d-flex align-items-center gap-2">
                <label className="mb-0 text-muted small fw-semibold">
                  Month
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 120 }}
                  value={chartMonth}
                  onChange={(e) => setChartMonth(e.target.value)}
                  disabled={comparisonMode !== "MoM"}
                >
                  <option value="all">All Months</option>
                  {MONTHS.map((m, i) => (
                    <option key={m} value={String(i + 1)}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ms-auto">
                <div className="btn-group" role="group">
                  {["YoY", "QoQ", "MoM"].map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      className={`btn btn-sm ${comparisonMode === mode ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setComparisonMode(mode)}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* KPI strip */}
            <div
              className="d-flex flex-wrap gap-0"
              style={{ borderBottom: "1px solid #f0f2f5" }}
            >
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: `4px solid ${C_CURRENT}`,
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Current · {currentLabel}
                </span>
                <span className="fs-5 fw-bold" style={{ color: C_CURRENT }}>
                  {fmtINR(currentTotal)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderRight: "1px solid #f0f2f5",
                  borderLeft: `4px solid ${C_PRIOR}`,
                }}
              >
                <span className="d-block text-muted small mb-1">
                  Prior · {priorLabel}
                </span>
                <span className="fs-5 fw-bold" style={{ color: C_PRIOR }}>
                  {fmtINR(priorTotal)}
                </span>
              </div>
              <div
                className="flex-fill px-4 py-3"
                style={{
                  borderLeft: `4px solid ${rawChange === null ? "#adb5bd" : changeUp ? "#dc3545" : "#198754"}`,
                }}
              >
                <span className="d-block text-muted small mb-1">Change</span>
                <span
                  className="fs-5 fw-bold"
                  style={{
                    color:
                      rawChange === null
                        ? "#adb5bd"
                        : changeUp
                          ? "#dc3545"
                          : "#198754",
                  }}
                >
                  {rawChange === null
                    ? "—"
                    : `${changeUp ? "▲" : "▼"} ${Math.abs(Number(rawChange))}%`}
                </span>
              </div>
            </div>

            {/* Bar chart */}
            <div className="card-body px-3 pt-3 pb-2">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div>
                  <h6 className="mb-0 fw-semibold">
                    Vehicle Expense Comparison
                  </h6>
                  <small className="text-muted">
                    {comparisonMode === "YoY" &&
                      "Year-over-Year · Monthly breakdown"}
                    {comparisonMode === "QoQ" &&
                      "Quarter-over-Quarter · Monthly breakdown within quarter"}
                    {comparisonMode === "MoM" &&
                      "Month-over-Month · Weekly breakdown"}
                  </small>
                </div>
                <span
                  className="badge fw-semibold px-3 py-2"
                  style={{
                    background: "#ede9fe",
                    color: C_CURRENT,
                    fontSize: 12,
                  }}
                >
                  {comparisonMode}
                </span>
              </div>

              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                  barCategoryGap="28%"
                  barGap={4}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#f0f2f5"
                  />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 12, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={fmtINR}
                    tick={{ fontSize: 11, fill: "#6c757d" }}
                    axisLine={false}
                    tickLine={false}
                    width={90}
                  />
                  <Tooltip
                    content={<ChartTooltip />}
                    cursor={{ fill: "#f8f9fa" }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                    iconType="circle"
                    iconSize={10}
                  />
                  <Bar
                    dataKey="current"
                    name={currentLabel}
                    fill={C_CURRENT}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                  <Bar
                    dataKey="prior"
                    name={priorLabel}
                    fill={C_PRIOR}
                    radius={[4, 4, 0, 0]}
                    maxBarSize={48}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* ══ CHART 2 — Monthly Expense Trend ══ */}
        <div className="col-lg-6 d-flex flex-column">
          <div className="card mb-4 h-100 d-flex flex-column">
            <div
              className="card-header d-flex flex-wrap align-items-center gap-3 py-3"
              style={{ background: "#fff", borderBottom: "1px solid #f0f2f5" }}
            >
              <div>
                <h6 className="mb-0 fw-semibold">Monthly Expense Trend</h6>
                <small className="text-muted">
                  Total vehicle spend · month by month
                </small>
              </div>
              <div className="d-flex align-items-center gap-2 ms-auto">
                <label className="mb-0 text-muted small fw-semibold">
                  Year
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 88 }}
                  value={trendYear}
                  onChange={(e) => setTrendYear(Number(e.target.value))}
                >
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              {trendHasData && (
                <span
                  className="badge fw-semibold px-3 py-2"
                  style={{
                    background: "#ede9fe",
                    color: C_CURRENT,
                    fontSize: 12,
                  }}
                >
                  Total {fmtINR(trendTotal)}
                </span>
              )}
            </div>

            <div className="card-body px-3 pt-3 pb-2 flex-grow-1 d-flex flex-column">
              {!trendHasData ? (
                <NoData year={trendYear} />
              ) : (
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={trendData}
                      margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
                    >
                      <defs>
                        <linearGradient
                          id="vehicleSummaryGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={C_CURRENT}
                            stopOpacity={0.25}
                          />
                          <stop
                            offset="95%"
                            stopColor={C_CURRENT}
                            stopOpacity={0.02}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#f0f2f5"
                      />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12, fill: "#6c757d" }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={fmtINR}
                        tick={{ fontSize: 11, fill: "#6c757d" }}
                        axisLine={false}
                        tickLine={false}
                        width={90}
                      />
                      <Tooltip
                        content={<ChartTooltip />}
                        cursor={{
                          stroke: C_CURRENT,
                          strokeWidth: 1,
                          strokeDasharray: "4 4",
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 13, paddingTop: 12 }}
                        iconType="circle"
                        iconSize={10}
                      />
                      <Area
                        type="monotone"
                        dataKey="expense"
                        name={`Expense ${trendYear}`}
                        stroke={C_CURRENT}
                        strokeWidth={2.5}
                        fill="url(#vehicleSummaryGrad)"
                        dot={{ r: 4, fill: C_CURRENT, strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: C_CURRENT }}
                        connectNulls={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ══ Summary table ══ */}
      <div className="row pb-4">
        <div className="col-lg-12">
          <div
            className="card mb-3"
            style={{ background: "#f8f9fa", height: "90px" }}
          >
            <div className="card-body d-flex align-items-center gap-4">
              <div>
                <span className="text-muted small d-block">
                  Total Vehicles with Expenses
                </span>
                <span className="fw-bold fs-5">
                  {loading ? "…" : rows.length}
                </span>
              </div>
              <div className="ms-auto text-end">
                <span className="text-muted small d-block">
                  Grand Total Expense
                </span>
                <span className="fw-bold fs-5 text-danger">
                  ₹ {loading ? "…" : fmt(grandTotal)}
                </span>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h4 className="card-title mb-0">Expense Summary by Vehicle</h4>
              <span className="badge badge-primary ms-2">{rows.length}</span>
            </div>
            <div className="card-body">
              {isError && (
                <div className="alert alert-danger">
                  Failed to load vehicle summaries. Please try again.
                </div>
              )}
              <div className="table-responsive">
                <table className="display dataTable no-footer w-100">
                  <thead>
                    <tr>
                      {/* <th>#</th> */}
                      <th>Vehicle Number</th>
                      <th>Make / Model</th>
                      <th>No. of Transactions</th>
                      <th>Total Expense (₹)</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr>
                        <td colSpan={6} className="text-center py-4">
                          <span className="spinner-border spinner-border-sm me-2" />
                          Loading…
                        </td>
                      </tr>
                    )}
                    {!loading && rows.length === 0 && !isError && (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          No vehicle expense data found.
                        </td>
                      </tr>
                    )}
                    {!loading &&
                      rows.map((row, idx) => (
                        <tr key={row._id || idx}>
                          {/* <td className="text-muted small">{idx + 1}</td> */}
                          <td
                            className="fw-semibold"
                            style={{ textTransform: "uppercase" }}
                          >
                            {row.vehicleNumber}
                          </td>
                          <td
                            className="text-muted small"
                            style={{ textTransform: "capitalize" }}
                          >
                            {row.vehicleMake}
                            {row.vehicleModel ? ` / ${row.vehicleModel}` : ""}
                          </td>
                          <td>{row.txnCount}</td>
                          <td className="fw-semibold text-danger">
                            ₹ {fmt(row.totalExpense)}
                          </td>
                          <td>
                            <button
                              className="btn btn-xs btn-outline-primary"
                              onClick={() =>
                                navigate(
                                  `/payments/finance/vehicle-detail/${row._id}`,
                                )
                              }
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    {!loading && rows.length > 0 && (
                      <tr className="table-dark fw-bold">
                        <td colSpan={4} className="text-end pe-3">
                          Grand Total
                        </td>
                        <td colSpan={2}>₹ {fmt(grandTotal)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default VehicleSummary;
